import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Shared renderer for the canonical legal markdown in src/legal/.
// Tables scroll horizontally inside their own wrapper so wide schedules
// never make the page scroll sideways on narrow screens.
const LINE = "rgba(0,0,0,0.08)";

type HastNode = { type: string; tagName?: string; children?: HastNode[] };

// Column count of a GFM table, read from its first row.
function columnCount(node?: HastNode): number {
  const stack = node ? [node] : [];
  while (stack.length) {
    const n = stack.shift()!;
    if (n.tagName === "tr") return (n.children ?? []).filter((c) => c.type === "element").length;
    stack.push(...(n.children ?? []));
  }
  return 1;
}

export default function LegalMarkdown({ markdown }: { markdown: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ children }) => (
          <h2
            style={{ fontFamily: "'Instrument Serif', serif", fontSize: 34, fontWeight: 400, margin: "48px 0 16px" }}
          >
            {children}
          </h2>
        ),
        h2: ({ children }) => (
          <h3
            style={{ fontFamily: "'Instrument Serif', serif", fontSize: 27, fontWeight: 400, margin: "36px 0 12px" }}
          >
            {children}
          </h3>
        ),
        p: ({ children }) => <p style={{ margin: "0 0 16px" }}>{children}</p>,
        ul: ({ children }) => <ul style={{ margin: "0 0 20px", paddingLeft: 24 }}>{children}</ul>,
        ol: ({ children, start }) => (
          <ol start={start} style={{ margin: "0 0 20px", paddingLeft: 24, listStyle: "decimal" }}>
            {children}
          </ol>
        ),
        li: ({ children }) => <li style={{ marginBottom: 8 }}>{children}</li>,
        a: ({ children, href }) => (
          <a href={href} style={{ color: "#8f5f26" }}>
            {children}
          </a>
        ),
        strong: ({ children }) => <strong style={{ color: "#1a1a1a" }}>{children}</strong>,
        table: ({ children, node }) => (
          <div
            role="region"
            aria-label="Table"
            tabIndex={0}
            style={{
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              maxWidth: "100%",
              margin: "0 0 24px",
              border: `1px solid ${LINE}`,
              borderRadius: 8,
              background: "#FFFFFF",
            }}
          >
            <table style={{ borderCollapse: "collapse", width: "100%", minWidth: Math.max(300, columnCount(node as HastNode) * 105), fontSize: 14, lineHeight: 1.45 }}>
              {children}
            </table>
          </div>
        ),
        th: ({ children, style }) => (
          <th
            style={{
              textAlign: "left",
              ...style,
              verticalAlign: "bottom",
              padding: "10px 12px",
              background: "#F8F4EC",
              color: "#1a1a1a",
              fontWeight: 600,
              borderBottom: "2px solid #C4823A",
            }}
          >
            {children}
          </th>
        ),
        td: ({ children, style }) => (
          <td
            style={{
              textAlign: "left",
              ...style,
              verticalAlign: "top",
              padding: "10px 12px",
              borderBottom: `1px solid ${LINE}`,
            }}
          >
            {children}
          </td>
        ),
      }}
    >
      {markdown}
    </ReactMarkdown>
  );
}
